/**
 * UAT hotfix (2026-07-25) · Cost Centre master admin surface.
 * Enterprise org-level master. Strict 1 BU → many CCs cardinality.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";


export default function CostCentreMasterPage() {
    const [items, setItems] = useState([]);
    const [businessUnits, setBusinessUnits] = useState([]);
    const [buFilter, setBuFilter] = useState("");
    const [includeInactive, setIncludeInactive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ code: "", name: "", business_unit_id: "", description: "" });

    useEffect(() => {
        api.get("/org/business-unit/list?page_size=200")
            .then((r) => setBusinessUnits(r.data?.items || []))
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams({
            include_inactive: String(includeInactive),
            page_size: "200",
        });
        if (buFilter) params.set("business_unit_id", buFilter);
        api.get(`/org/cost-centre/list?${params.toString()}`)
            .then((r) => setItems(r.data?.items || []))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [buFilter, includeInactive]);

    const buName = (id) => businessUnits.find((bu) => bu.id === id)?.name || id;

    const startCreate = () => {
        setEditing(null);
        setForm({ code: "", name: "", business_unit_id: buFilter || "", description: "" });
        setDialogOpen(true);
    };
    const startEdit = (cc) => {
        setEditing(cc);
        setForm({
            code: cc.code, name: cc.name,
            business_unit_id: cc.business_unit_id, description: cc.description || "",
        });
        setDialogOpen(true);
    };
    const save = async () => {
        try {
            if (editing) {
                await api.patch(`/org/cost-centre/${editing.id}`, {
                    name: form.name, description: form.description,
                });
                toast.success("Cost Centre updated.");
            } else {
                await api.post("/org/cost-centre", form);
                toast.success("Cost Centre created.");
            }
            setDialogOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const toggleActive = async (cc) => {
        try {
            await api.patch(`/org/cost-centre/${cc.id}`, { is_active: !cc.is_active });
            toast.success(`Cost Centre ${cc.is_active ? "deactivated" : "reactivated"}.`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="p-6 space-y-4" data-testid="cc-master-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Cost Centre Master</h1>
                    <p className="text-sm text-slate-500">
                        Every Cost Centre belongs to exactly one Business Unit.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={buFilter || "__all__"} onValueChange={(v) => setBuFilter(v === "__all__" ? "" : v)}>
                        <SelectTrigger data-testid="cc-bu-filter"><SelectValue placeholder="All Business Units" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All Business Units</SelectItem>
                            {businessUnits.map((bu) => (
                                <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1 text-xs text-slate-500">
                        <input type="checkbox" checked={includeInactive}
                            onChange={(e) => setIncludeInactive(e.target.checked)} />
                        Include inactive
                    </label>
                    <Button onClick={startCreate}
                        disabled={businessUnits.length === 0}
                        data-testid="cc-create-btn">+ New Cost Centre</Button>
                </div>
            </div>
            {businessUnits.length === 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    No Business Units. Create at least one Business Unit before adding Cost Centres.
                </div>
            )}
            <div className="border rounded-md">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Business Unit</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={5} className="text-center text-slate-400">Loading...</TableCell></TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-slate-400 py-6">
                                    No Cost Centres in scope.
                                </TableCell>
                            </TableRow>
                        )}
                        {items.map((cc) => (
                            <TableRow key={cc.id}>
                                <TableCell className="font-mono text-xs">{cc.code}</TableCell>
                                <TableCell>{cc.name}</TableCell>
                                <TableCell className="text-xs text-slate-500">{buName(cc.business_unit_id)}</TableCell>
                                <TableCell>
                                    <Badge variant={cc.is_active ? "default" : "secondary"}>
                                        {cc.is_active ? "Active" : "Inactive"}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right space-x-2">
                                    <Button size="sm" variant="outline" onClick={() => startEdit(cc)}>Edit</Button>
                                    <Button size="sm" variant="outline" onClick={() => toggleActive(cc)}>
                                        {cc.is_active ? "Deactivate" : "Reactivate"}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="cc-form-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Cost Centre" : "New Cost Centre"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Business Unit <span className="text-rose-600">*</span></Label>
                            <Select value={form.business_unit_id}
                                onValueChange={(v) => setForm({ ...form, business_unit_id: v })}
                                disabled={!!editing}>
                                <SelectTrigger data-testid="cc-form-bu"><SelectValue placeholder="Choose Business Unit..." /></SelectTrigger>
                                <SelectContent>
                                    {businessUnits.filter((bu) => bu.is_active).map((bu) => (
                                        <SelectItem key={bu.id} value={bu.id}>{bu.name} ({bu.code})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Code <span className="text-rose-600">*</span></Label>
                            <Input value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                disabled={!!editing}
                                placeholder="e.g. CC-OPS-101" data-testid="cc-form-code" />
                        </div>
                        <div>
                            <Label>Name <span className="text-rose-600">*</span></Label>
                            <Input value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Delivery – India" data-testid="cc-form-name" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={save} disabled={!form.business_unit_id || !form.code || !form.name}>
                            {editing ? "Save changes" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
